import { useState, useRef, useCallback, useEffect } from 'react';
import { UseAudioRecorderReturn } from '../types';
import useSounds from './useSounds';

// Minimum RMS threshold to consider audio as containing speech
// Values below this are considered silence
const SILENCE_THRESHOLD = 0.01;

// Recording time limits in seconds
const MAX_RECORDING_TIME = 120; // 2 minutes

/**
 * Analyze audio blob to detect if it contains actual sound or is silent
 * Returns true if audio contains sound above the threshold
 */
const analyzeAudioForSilence = async (blob: Blob): Promise<boolean> => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Get the audio data from the first channel
    const channelData = audioBuffer.getChannelData(0);

    // Calculate RMS (Root Mean Square) to measure audio level
    let sumSquares = 0;
    for (let i = 0; i < channelData.length; i++) {
      sumSquares += channelData[i] * channelData[i];
    }
    const rms = Math.sqrt(sumSquares / channelData.length);

    // Close the audio context
    await audioContext.close();

    // Return true if audio level is above threshold (has sound)
    return rms > SILENCE_THRESHOLD;
  } catch (err) {
    console.error('Error analyzing audio:', err);
    // If analysis fails, assume audio is valid to avoid blocking users
    return true;
  }
};

const useAudioRecorder = (): UseAudioRecorderReturn => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const { playSound } = useSounds();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recordingStartTimeRef = useRef<number>(0);

  // Timer effect - updates every second while recording
  useEffect(() => {
    if (isRecording) {
      recordingStartTimeRef.current = Date.now();
      setRecordingTime(0);

      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
        setRecordingTime(elapsed);

        // Auto-stop at max recording time
        if (elapsed >= MAX_RECORDING_TIME) {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            playSound('stopRecording');
          }
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording, playSound]);

  const startRecording = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      setAudioBlob(null);
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });

        // Stop all tracks to release the microphone
        stream.getTracks().forEach(track => track.stop());

        // Validate audio contains actual sound
        const hasSound = await analyzeAudioForSilence(blob);
        if (!hasSound) {
          setError("I can't hear you. Please check your microphone settings and try again.");
          return;
        }

        setAudioBlob(blob);
      };

      mediaRecorder.onerror = () => {
        setError('Recording error occurred');
      };

      mediaRecorder.start();
      setIsRecording(true);
      playSound('startRecording');
    } catch (err) {
      const error = err as Error & { name: string };
      if (error.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access and try again.');
      } else if (error.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError('Failed to start recording. Please try again.');
      }
      console.error('Error starting recording:', err);
    }
  }, [playSound]);

  const stopRecording = useCallback((): void => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      playSound('stopRecording');
    }
  }, [isRecording, playSound]);

  const resetRecording = useCallback((): void => {
    // Stop any ongoing playback
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
    setIsPlaying(false);
    setAudioBlob(null);
    setError(null);
  }, []);

  const playRecording = useCallback((): void => {
    if (!audioBlob) {
      return;
    }

    // Stop any existing playback
    if (audioElementRef.current) {
      audioElementRef.current.pause();
    }

    // Create audio element and play
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    audioElementRef.current = audio;

    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => {
      setIsPlaying(false);
      URL.revokeObjectURL(audioUrl);
      audioElementRef.current = null;
    };
    audio.onerror = () => {
      setIsPlaying(false);
      setError('Failed to play recording');
      URL.revokeObjectURL(audioUrl);
      audioElementRef.current = null;
    };

    audio.play().catch(() => {
      setIsPlaying(false);
      setError('Failed to play recording');
      URL.revokeObjectURL(audioUrl);
      audioElementRef.current = null;
    });
  }, [audioBlob]);

  const stopPlayback = useCallback((): void => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  return {
    isRecording,
    audioBlob,
    error,
    recordingTime,
    startRecording,
    stopRecording,
    resetRecording,
    isPlaying,
    playRecording,
    stopPlayback
  };
};

export default useAudioRecorder;
